import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Dealership from '../../../types/dealership/Dealership';
import DealershipWriteDTO from '../../../types/dealership/DealershipWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class DealershipService {

    private RESOURCE: string = 'dealership';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the dealerships from the backend.
     *
     * @param search The search query to filter the dealerships by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The dealerships.
     */
    public async getDealerships(search?: string, page: number = 0, size: number = 10): Promise<Page<Dealership>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Dealership>>(this.RESOURCE, params);
    }

    /**
     * Fetches all dealerships from the backend.
     *
     * @return The dealerships.
     */
    public async getAllDealerships(): Promise<Dealership[]> {
        return await this.baseService.get<Dealership[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a dealership from the backend.
     *
     * @param id The ID of the dealership to fetch.
     * @return The dealership.
     */
    public async getDealership(id: number): Promise<Dealership> {
        return await this.baseService.get<Dealership>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a dealership in the backend.
     *
     * @param dealership The dealership to create.
     * @return The response from the backend.
     */
    public async createDealership(dealership: DealershipWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, dealership);
    }

    /**
     * Updates a dealership in the backend.
     *
     * @param dealership The dealership to update.
     * @return The response from the backend.
     */
    public async updateDealership(dealership: DealershipWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + dealership.id, dealership);
    }

    /**
     * Deletes a dealership from the backend.
     *
     * @param id The ID of the dealership to delete.
     * @return The response from the backend.
     */
    public async deleteDealership(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
